import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Grid,
  Icon,
  Tooltip,
  TextField,
  MenuItem,
  FormControl,
  Select,
  RadioGroup,
  Radio,
  FormControlLabel,
} from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DataTable from "examples/Tables/DataTable";
import MDBadge from "components/MDBadge";
import CenterPopup from "components/CommonComponent/RightPanelDrawer";
import { useNotify } from "components/CommonComponent/NotificationProvider";
import { STATUS } from "components/common/constant";
import RequiredLabel from "components/CommonComponent/RequiredLabel";
import { showAlert } from "components/CommonComponent/ShowAlert";
import {
  selectSx,
  radioLabelSx,
  ISOToDDMMYYYY,
  wrapCell,
} from "components/CommonComponent/CommonFunction";
import {
  addShift,
  getShiftList,
  getShiftById,
  updateShiftById,
  deleteShiftById,
} from "actions/shift";
import { useDispatch, useSelector } from "react-redux";
import { shiftList } from "constants/shift";
import {
  DEFAULT_RECORDS_PER_PAGE,
  hasAccess,
} from "components/common/constant";

const initialFields = {
  id: null,
  name: { value: "", original: "", error: "" },
  startTime: { value: "", original: "", error: "" },
  endTime: { value: "", original: "", error: "" },
  totalHours: { value: "", original: "", error: "" },
  status: { value: "ACTIVE", original: "ACTIVE", error: "" },
};

function ShiftListing() {
  const startRef = useRef(null);
  const endRef = useRef(null);

  const dispatch = useDispatch();
  const shiftListData = useSelector((state) => state.shift.shiftList);
  const { notify } = useNotify();
  const authData = JSON.parse(localStorage.getItem("p")) || {};
  const currentRole = authData?.role;

  const canCreate = hasAccess("SHIFT_MASTER", "CREATE")?.includes(currentRole);
  const canUpdate = hasAccess("SHIFT_MASTER", "UPDATE")?.includes(currentRole);
  const canDelete = hasAccess("SHIFT_MASTER", "DELETE")?.includes(currentRole);

  const canShowActions = canUpdate || canDelete;
  const [pageNum, setPageNum] = useState(1);
  const [panelMode, setPanelMode] = useState(null); // add | edit
  const [shiftFormData, setShiftFormData] = useState(initialFields);
  const [listState, setListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  const [filters, setFilters] = useState({
    name: "",
    status: "",
    fromDate: "",
    toDate: "",
    filterParams: [],
  });

  useEffect(() => {
    getShiftList(dispatch, pageNum, filters.filterParams);

    return () => {
      dispatch({ type: shiftList, payload: {} });
    };
  }, [dispatch, pageNum, filters.filterParams]);

  useEffect(() => {
    if (shiftListData?.code === 200) {
      setListState({
        isLoading: false,
        error: "",
        noRecords: shiftListData?.data?.shift?.length === 0,
        showLoaderOnClick: false,
      });
    } else if (shiftListData?.code || shiftListData?.message) {
      setListState({
        isLoading: false,
        error:
          shiftListData?.message ||
          "Failed to load shift list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [shiftListData]);

  const tableData =
    shiftListData?.data?.shift?.map((item, index) => ({
      ...item,
      id: item?.ID,
      srNo:
        (pageNum - 1) *
          (shiftListData?.data?.noOfRecordsPerPage ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
      name: item?.name || "-",
      startTime: item?.startTime || "-",
      endTime: item?.endTime || "-",
      formattedHours: item?.formattedHours || "0.00",
      createdAt: ISOToDDMMYYYY(item?.createdAt) || "-",
      status: item?.status || "-",
    })) || [];

  const buildFilterParams = () => {
    const filterParams = [];

    if (filters.name?.trim()) {
      filterParams.push({
        field: "Name",
        condition: "like",
        filterValues: [filters.name.trim()],
      });
    }

    if (filters.status) {
      filterParams.push({
        field: "Status",
        condition: "eq",
        filterValues: [filters.status],
      });
    }

    return filterParams.length > 0 ? JSON.stringify(filterParams) : [];
  };

  const getValidationError = (name, value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    switch (name) {
      case "name":
        return !trimmed ? "Please enter shift name" : "";

      case "startTime":
        return !trimmed ? "Please enter start time" : "";
      case "endTime":
        return !trimmed ? "Please enter end time" : "";
      case "totalHours":
        return !trimmed ? "Please enter total hours" : "";
      default:
        return "";
    }
  };

  const handleChange = (field, value) => {
    setShiftFormData((prev) => {
      const updated = {
        ...prev,
        [field]: { ...prev[field], value, error: "" },
      };
      // auto calculate when start or end changes
      if (field === "startTime" || field === "endTime") {
        const start = field === "startTime" ? value : prev.startTime.value;
        const end = field === "endTime" ? value : prev.endTime.value;

        const total = calculateHours(start, end);
        updated.totalHours = {
          ...prev.totalHours,
          value: total,
          error: "",
        };
      }
      return updated;
    });
  };

  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setShiftFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  const validateForm = () => {
    let newData = { ...shiftFormData };
    Object.keys(newData).forEach((key) => {
      if (key === "id") return;
      newData[key] = {
        ...newData[key],
        error: getValidationError(key, newData[key].value),
      };
    });
    setShiftFormData(newData);
    return Object.keys(newData)
      .filter((key) => key !== "id")
      .every((key) => newData[key].error === "");
  };

  const handleClear = () => {
    setFilters({
      name: "",
      status: "",
      fromDate: "",
      toDate: "",
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  const openAddModal = () => {
    setPanelMode("add");
    setShiftFormData(initialFields);
  };

  const openEditModal = async (row) => {
    setPanelMode("edit");

    setListState((prev) => ({
      ...prev,
      isLoading: true,
    }));

    const res = await getShiftById(row.id);

    if (res?.code === 200) {
      const dept = res?.data;

      setShiftFormData({
        id: dept?.ID,
        name: {
          value: dept?.name || "",
          original: dept?.name || "",
          error: "",
        },
        startTime: {
          value: dept?.startTime || "",
          original: dept?.startTime || "",
          error: "",
        },
        endTime: {
          value: dept?.endTime || "",
          original: dept?.endTime || "",
          error: "",
        },
        totalHours: {
          value: dept?.totalHours || "",
          original: dept?.totalHours || "",
          error: "",
        },
        status: {
          value: dept?.status?.toUpperCase() || "ACTIVE",
          original: dept?.status?.toUpperCase() || "ACTIVE",
          error: "",
        },
      });
    } else {
      const message = Array.isArray(res?.message)
        ? res?.message
            .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
            .join(", ")
        : res?.message || "Failed to fetch shift details";
      notify(message, "error");
    }

    setListState((prev) => ({
      ...prev,
      isLoading: false,
    }));
  };

  const closeModal = () => {
    setPanelMode(null);
    setShiftFormData({ ...initialFields });
  };

  const handleSearch = () => {
    const newFilterParams = buildFilterParams();

    if (
      JSON.stringify(filters.filterParams) !== JSON.stringify(newFilterParams)
    ) {
      setListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));

      setFilters((prev) => ({
        ...prev,
        filterParams: newFilterParams,
      }));

      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleDelete = (row) => {
    showAlert({
      title: "Are you sure you want to delete this shift?",
      message: `Shift "${row.name}" will be deleted permanently.`,
      type: "warning",
      showCancel: true,
      confirmText: "Yes, delete",
      cancelText: "Cancel",

      onConfirm: async () => {
        try {
          setListState((prev) => ({
            ...prev,
            isLoading: true,
          }));

          const res = await deleteShiftById(row.id);

          if (res?.code === 200) {
            notify(res?.message || "Shift deleted successfully", "success");

            // refresh list
            getShiftList(dispatch, pageNum, filters.filterParams);
          } else {
            notify(res?.message || "Failed to delete shift", "error");
          }
        } catch (error) {
          notify(error?.message || "Something went wrong", "error");
        } finally {
          setListState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      },

      onCancel: () => {},
    });
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: true,
      }));

      let payload = {};

      Object.keys(shiftFormData).forEach((key) => {
        if (key === "id") return;

        const field = shiftFormData[key];

        if (panelMode !== "edit" || field.value !== field.original) {
          if (key === "totalHours") {
            payload[key] = Number(field.value);
          } else {
            payload[key] = field.value;
          }
        }
      });

      if (panelMode === "edit" && !Object.keys(payload).length) {
        notify("No changes detected", "info");
        setListState((prev) => ({
          ...prev,
          showLoaderOnClick: false,
        }));
        return;
      }

      const res =
        panelMode === "edit"
          ? await updateShiftById(shiftFormData.id, payload)
          : await addShift(payload);

      if (res?.code === 200) {
        notify(
          res?.message ||
            (panelMode === "edit"
              ? "Shift updated successfully"
              : "Shift added successfully"),
          "success",
        );

        closeModal();
        getShiftList(dispatch, pageNum, filters.filterParams);
      } else {
        const message = Array.isArray(res?.message)
          ? res.message
              .map((err) => (err.Msg ? err.Msg : JSON.stringify(err)))
              .join(", ")
          : res?.message || "Failed to save shift";

        notify(message, "error");
      }
    } catch (error) {
      notify(error?.message || "Something went wrong", "error");
    } finally {
      setListState((prev) => ({
        ...prev,
        showLoaderOnClick: false,
      }));
    }
  };

  const renderStatusBadge = (status) => {
    if (!status) return null;
    const s = typeof status === "string" ? status.toUpperCase() : status;
    return (
      <MDBadge
        badgeContent={s}
        color={s === "ACTIVE" ? "success" : "secondary"}
        variant="gradient"
        size="sm"
        circular
      />
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const columns = [
    { Header: "Sr No.", accessor: "srNo", align: "left" },
    { Header: "Shift Name", accessor: "name", align: "left" },
    { Header: "Start Time", accessor: "startTime", align: "center" },
    { Header: "End Time", accessor: "endTime", align: "center" },
    { Header: "Total Hours", accessor: "formattedHours", align: "center" },
    {
      Header: "Status",
      accessor: "status",
      align: "center",
      Cell: (cell) => renderStatusBadge(cell.row.original.status),
    },
    { Header: "Created At", accessor: "createdAt", align: "center" },

    ...(canShowActions
      ? [
          {
            Header: "Action",
            accessor: "actions",
            align: "center",
            disableSortBy: true,
            Cell: (cell) => {
              const rowData = cell.row.original;

              return (
                <MDBox
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  {canUpdate && (
                    <Tooltip title="Edit" arrow>
                      <Icon
                        sx={({ palette }) => ({
                          cursor: "pointer",
                          color: palette.secondary.main,
                        })}
                        onClick={() => openEditModal(rowData)}
                      >
                        <i className="fas fa-edit fa-sm" />
                      </Icon>
                    </Tooltip>
                  )}

                  {canUpdate && canDelete && (
                    <MDTypography variant="caption" mx={0.5}>
                      |
                    </MDTypography>
                  )}

                  {canDelete && (
                    <Tooltip title="Delete" arrow>
                      <Icon
                        color="error"
                        sx={{
                          cursor: "pointer",
                          opacity: 0.5,
                          "&:hover": { opacity: 1 },
                        }}
                        onClick={() => handleDelete(rowData)}
                      >
                        <i className="fas fa-trash" />
                      </Icon>
                    </Tooltip>
                  )}
                </MDBox>
              );
            },
          },
        ]
      : []),
  ];

  const calculateHours = (start, end) => {
    if (!start || !end) return "";
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let startDate = new Date();
    let endDate = new Date();
    startDate.setHours(sh, sm, 0);
    endDate.setHours(eh, em, 0);

    // handle overnight shift (e.g. 10 PM → 6 AM)
    if (endDate < startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }
    const diffMs = endDate - startDate;
   
    // convert to total minutes
    const totalMinutes = Math.floor(diffMs / (1000 * 60));

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // keep existing functionality, only format changed
    return `${hours}.${minutes.toString().padStart(2, "0")}`;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <MDBox width="100%">
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              {/* Header */}
              <MDBox
                px={3}
                pt={3}
                pb={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h5" fontWeight="bold">
                  Shifts
                </MDTypography>

                {canCreate && (
                  <MDButton
                    type="button"
                    variant="contained"
                    color="deepBlue"
                    sx={{
                      py: 1.2,
                      px: 3,
                      fontWeight: "bold",
                      fontSize: "0.9rem",
                      textTransform: "none",
                    }}
                    onClick={openAddModal}
                  >
                    Add Shift
                  </MDButton>
                )}
              </MDBox>

              {/* Filters */}
              <MDBox px={3} pb={2} onKeyDown={handleKeyDown}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="Search by shift name"
                      value={filters.name}
                      onChange={(e) =>
                        setFilters({ ...filters, name: e.target.value })
                      }
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={2}>
                    <FormControl fullWidth size="small" sx={selectSx("100%")}>
                      <Select
                        value={filters.status || ""}
                        displayEmpty
                        onChange={(e) =>
                          setFilters({ ...filters, status: e.target.value })
                        }
                        IconComponent={ArrowDropDown}
                        renderValue={(selected) => {
                          if (!selected)
                            return (
                              <span style={{ color: "rgba(0, 0, 0, 0.38)" }}>
                                Select Status
                              </span>
                            );
                          return renderStatusBadge(selected);
                        }}
                      >
                        {STATUS.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {renderStatusBadge(status.value)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6} md={4} lg={3} xl={2}>
                    <MDBox
                      display="flex"
                      gap={1}
                      flexWrap="nowrap"
                      width="100%"
                    >
                      <MDButton
                        variant="contained"
                        color="deepBlue"
                        sx={{
                          flex: 1,
                          minWidth: "90px",
                          maxWidth: "160px",
                          whiteSpace: "nowrap",
                        }}
                        onClick={handleSearch}
                        disabled={listState.showLoaderOnClick}
                      >
                        {listState.showLoaderOnClick ? (
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        ) : (
                          <>
                            <Icon sx={{ mr: 1 }}>search</Icon>
                            Search
                          </>
                        )}
                      </MDButton>

                      {filters.filterParams.length > 0 && (
                        <MDButton
                          variant="contained"
                          color="deepBlue"
                          sx={{
                            flex: 1,
                            minWidth: "90px",
                            maxWidth: "160px",
                            whiteSpace: "nowrap",
                          }}
                          onClick={handleClear}
                        >
                          <Icon sx={{ mr: 1 }}>close</Icon>
                          Clear
                        </MDButton>
                      )}
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Total Records */}
              <MDBox px={3} py={1}>
                <MDTypography variant="body1" color="deepBlue" mb={1}>
                  Total Records: {shiftListData?.data?.totalRecords || 0}
                </MDTypography>
              </MDBox>

              {/* Table */}
              <MDBox sx={{ width: "100%", overflowX: "auto" }}>
                {listState.isLoading ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </MDBox>
                ) : listState.error ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="error">
                      {listState.error}
                    </MDTypography>
                  </MDBox>
                ) : listState.noRecords ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="150px"
                  >
                    <MDTypography variant="body2" color="text">
                      No shifts found.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    columns={columns}
                    data={tableData}
                    isSorted={true}
                    pageNum={pageNum}
                    setPageNum={setPageNum}
                    totalPages={Math.ceil(
                      (shiftListData?.data?.totalRecords || 0) /
                        (shiftListData?.data?.noOfRecordsPerPage ||
                          DEFAULT_RECORDS_PER_PAGE),
                    )}
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        {/* Right Drawer (Add / Edit) */}
        <CenterPopup isOpen={panelMode !== null} onClose={closeModal}>
          <MDBox sx={{ p: 2 }}>
            <MDTypography variant="h6" mb={2}>
              {panelMode === "edit" ? "Edit Shift" : "Add Shift"}
            </MDTypography>

            {listState.isLoading ? (
              <MDBox
                display="flex"
                justifyContent="center"
                alignItems="center"
                height="150px"
              >
                <i className="fa fa-spinner fa-spin fa-2xl loader" />
              </MDBox>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <RequiredLabel required>Shift Name</RequiredLabel>
                  <MDInput
                    variant="outlined"
                    type="text"
                    name="name"
                    placeholder="Enter shift name"
                    value={shiftFormData.name.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    error={!!shiftFormData.name.error}
                    fullWidth
                    inputProps={{ maxLength: 50 }}
                    InputProps={{
                      // startAdornment: (
                      //   <InputAdornment position="start">
                      //     <Icon fontSize="small">label</Icon>
                      //   </InputAdornment>
                      // ),
                      sx: { borderRadius: 2 },
                    }}
                  />
                  {shiftFormData.name.error && (
                    <MDTypography variant="caption" color="error">
                      {shiftFormData.name.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <RequiredLabel required>Start Time</RequiredLabel>
                  <MDInput
                    type="time"
                    name="startTime"
                    value={shiftFormData.startTime.value}
                    inputRef={startRef}
                    onClick={() => startRef.current?.showPicker?.()}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    fullWidth
                  />
                  {shiftFormData.startTime.error && (
                    <MDTypography variant="caption" color="error">
                      {shiftFormData.startTime.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <RequiredLabel required>End Time</RequiredLabel>
                  <MDInput
                    type="time"
                    name="endTime"
                    value={shiftFormData.endTime.value}
                    inputRef={endRef}
                    onClick={() => endRef.current?.showPicker?.()}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    fullWidth
                  />
                  {shiftFormData.endTime.error && (
                    <MDTypography variant="caption" color="error">
                      {shiftFormData.endTime.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel required>Total Hours</RequiredLabel>
                  <MDInput
                    variant="outlined"
                    type="number"
                    name="totalHours"
                    placeholder="Enter total hours"
                    value={shiftFormData.totalHours.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    error={!!shiftFormData.totalHours.error}
                    fullWidth
                    inputProps={{ min: 0 }}
                  />
                  {shiftFormData.totalHours.error && (
                    <MDTypography variant="caption" color="error">
                      {shiftFormData.totalHours.error}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12}>
                  <RequiredLabel>Status</RequiredLabel>
                  <RadioGroup
                    row
                    name="status"
                    value={shiftFormData.status.value}
                    onChange={(e) =>
                      handleChange(e.target.name, e.target.value)
                    }
                  >
                    <FormControlLabel
                      value="ACTIVE"
                      control={<Radio size="small" />}
                      label="Active"
                      sx={radioLabelSx}
                    />
                    <FormControlLabel
                      value="INACTIVE"
                      control={<Radio size="small" />}
                      label="Inactive"
                      sx={radioLabelSx}
                    />
                  </RadioGroup>
                </Grid>

                {/* Save / Cancel */}
                <Grid item xs={12}>
                  <MDBox display="flex" justifyContent="flex-end">
                    <MDButton
                      variant="gradient"
                      color="light"
                      onClick={closeModal}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </MDButton>

                    <MDButton
                      type="submit"
                      variant="contained"
                      color="deepBlue"
                      onClick={handleSubmit}
                      disabled={listState.showLoaderOnClick}
                    >
                      {listState.showLoaderOnClick ? (
                        <>
                          <i className="fa fa-spinner fa-spin fa-lg text-white" />
                        </>
                      ) : (
                        "Save"
                      )}
                    </MDButton>
                  </MDBox>
                </Grid>
              </Grid>
            )}
          </MDBox>
        </CenterPopup>
      </MDBox>
    </DashboardLayout>
  );
}

export default ShiftListing;
